import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import {
	fetchPlayerConfig,
	deleteVideoSession,
	setSelectedProgram,
	playerOnCancel
} from 'store/actions';

// Import styles
import { flex, transitionMain } from 'components/styles';

// Import helpers
import { createLinkParams, getImage } from 'helpers';
import { sendGTMEvent } from 'helpers/tracking';

// Import components
import { Paragraph } from 'components/elements';

// Import utilities
import { ImageOnLoadRPC } from 'components/utilities';

// Import variables
import { ROUTES_CORE_PATHS, PRODUCT_IMAGES_TYPES } from 'helpers/variables';

import {
	GTM_TRACKING_EVENTS,
	FILM_GTM_SEGMENTATION,
	CLICK_GTM_ACTION
} from 'helpers/tracking';

const { CLICK_SEARCH_LIST } = GTM_TRACKING_EVENTS;
const { TELEVISION } = ROUTES_CORE_PATHS;
const { LOGO, LOGO_INVERTED } = PRODUCT_IMAGES_TYPES;

const SearchItemChannel = ({
	toggle,
	product,
	channels,
	videoSessionId,
	fetchPlayerConfig,
	deleteVideoSession,
	setSelectedProgram,
	playerOnCancel
}) => {
	const { pathname } = useLocation();
	const channel = channels.find((channel) => channel.uuid === product.uuid);

	if (!channel) return null;

	const {
		title,
		images,
		uuid,
		type,
		epgProgrammes,
		currentLiveIndex,
		pc_rating,
		genres
	} = channel;

	// Get image url
	const imgUrl = getImage({ images, types: [LOGO, LOGO_INVERTED] });

	const handleLinkRedirect = async (e) => {
		if (pathname.includes(TELEVISION)) {
			e.preventDefault();
			// Delete request
			// Dispatch an playerOnCancel action in player folder
			playerOnCancel();
			// Delete video live session
			// Dispatch an deleteVideoSession action in player folder
			videoSessionId && (await deleteVideoSession());
			// Set title in player overlay content
			// Dispatch an setSelectedProgram action in channel folder
			const program = epgProgrammes[currentLiveIndex];
			program &&
				setSelectedProgram({
					title: program.title,
					id: program.uuid,
					channel: title,
					channelID: uuid
				});
			// Fetch new video session config
			// Dispatch an fetchPlayerConfig action in player folder
			fetchPlayerConfig({ uuid });
		}
		toggle();

		sendGTMEvent({
			event: CLICK_SEARCH_LIST,
			customEventData: {
				segmentation: FILM_GTM_SEGMENTATION,
				action: CLICK_GTM_ACTION,
				page_url: pathname,
				url: { url: link.pathname },
				genres,
				item_name: title,
				item_variant: pc_rating
			}
		});
	};

	const link = createLinkParams({ id: uuid, type });

	return (
		<SearchItemStyled>
			<LinkStyled onClick={handleLinkRedirect} to={link}>
				<Preview>
					{imgUrl && (
						<ImageOnLoadRPC>
							{({ setError }) => (
								<Image onError={setError} src={imgUrl} alt={title} />
							)}
						</ImageOnLoadRPC>
					)}
				</Preview>
				<Content>{title && <ParagraphStyled>{title}</ParagraphStyled>}</Content>
			</LinkStyled>
		</SearchItemStyled>
	);
};

const SearchItemStyled = styled.li`
	height: 9rem;
	border-top: 1px solid ${({ theme }) => theme.line};
`;

const Preview = styled.div`
	height: 7rem;
	width: 10rem;
	overflow: hidden;
	${flex()}
`;

const Image = styled.img`
	max-width: 100%;
	height: auto;
	max-height: 100%;
	width: auto;
`;

const Content = styled.div`
	flex: 1;
	overflow: hidden;
	padding-left: 2rem;
`;

const ParagraphStyled = styled(Paragraph.Ellipsis)`
	color: ${({ theme }) => theme.grayMedium};
	font-size: 1.5rem;
	${transitionMain}
`;

const LinkStyled = styled(Link)`
	&:link,
	&:visited {
		display: flex;
		align-items: center;
		height: 100%;
		width: 100%;
		padding: 1rem 1.5rem;
		${transitionMain}
	}

	&:hover,
	&:active {
		background: ${({ theme: { searchBox } }) => searchBox.itemHoverBg};

		${ParagraphStyled} {
			color: ${({ theme: { searchBox } }) => searchBox.itemTitleHover};
		}
	}
`;

SearchItemChannel.propTypes = {
	product: PropTypes.object.isRequired,
	toggle: PropTypes.func.isRequired,
	channels: PropTypes.array.isRequired
};

const mapStateToProps = ({ player: { videoSessionId } }) => ({
	videoSessionId
});

export default connect(
	mapStateToProps,
	{
		fetchPlayerConfig,
		deleteVideoSession,
		setSelectedProgram,
		playerOnCancel
	}
)(SearchItemChannel);
